import React, { useState } from 'react';
import {
  LayoutDashboardIcon,
  CalculatorIcon,
  StoreIcon,
  FileTextIcon,
  MegaphoneIcon,
  ClipboardCheckIcon,
  WarehouseIcon,
  ShieldAlertIcon,
  InboxIcon,
  PackageIcon,
  Building2Icon,
  UsersIcon,
  HeartPulseIcon,
  HistoryIcon,
} from 'lucide-react';
import NavItem from './NavItem';
import UserProfile from './UserProfile';
import AdminV2PortalLink from './AdminV2PortalLink';
import { usePermissions } from '../../hooks/usePermissions';
import { usePendingActionRequestsCount } from '../../hooks/api/useAdminActionRequests';
import { isCatalogEnabled, isStudioEnabled } from '../../lib/featureFlags';
import gbLogo from '../../assets/images/gb-logo.png';

/** Divider plus a small group name (hidden when collapsed), same as the brand sidebar's company group. */
const GroupLabel: React.FC<{ label: string; isCollapsed: boolean; divider?: boolean }> = ({
  label,
  isCollapsed,
  divider = true,
}) => (
  <>
    {divider && <div className="border-t border-white/30 my-2" />}
    {!isCollapsed && <div className="px-3 pb-1 text-xs text-white/60 truncate">{label}</div>}
  </>
);

interface AdminSidebarProps {
  isOpen: boolean;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ isOpen }) => {
  const { isSuperAdmin } = usePermissions();
  const [isHovered, setIsHovered] = useState(false);
  const { data: pendingRequests } = usePendingActionRequestsCount();

  const shouldExpand = isHovered;
  const collapsed = !shouldExpand;

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return (
    <div
      className={`bg-brand-navy text-white fixed inset-y-0 left-0 z-40 transform transition-all duration-300 ease-out ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0 ${shouldExpand ? 'w-64 shadow-2xl' : 'w-20'}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Logo Header */}
      <div
        className={`h-16 flex items-center justify-center border-b border-gray-700 transition-all duration-300 ease-out ${
          shouldExpand ? 'px-6' : 'px-2'
        }`}
      >
        <div className="flex items-center gap-2">
          <img
            className={`object-contain mix-blend-lighten brightness-200 transition-all duration-300 ease-out ${
              shouldExpand ? 'h-10' : 'h-7'
            }`}
            src={gbLogo}
            alt="GlobalBridge"
          />
          {shouldExpand && (
            <span className="text-xs font-semibold text-orange-400">
              Admin
            </span>
          )}
        </div>
      </div>

      <div className="h-[calc(100vh-4rem)] flex flex-col">
        <nav className="flex-1 mt-2 px-2 flex flex-col overflow-y-auto">
          {/* GB Metrics — our own view */}
          <GroupLabel label="GB Metrics" isCollapsed={collapsed} divider={false} />
          <div className="space-y-0.5">
            <NavItem icon={<LayoutDashboardIcon size={16} />} label="Dashboard" href="/admin/dashboard" isCollapsed={collapsed} matchSegments />
            <NavItem icon={<CalculatorIcon size={16} />} label="Reconcile" href="/admin/reconcile" isCollapsed={collapsed} matchSegments />
          </div>

          {/* Brand — what brands see, viewed as them */}
          <GroupLabel label="Brand" isCollapsed={collapsed} />
          <div className="space-y-0.5">
            {isStudioEnabled() && (
              <NavItem icon={<FileTextIcon size={16} />} label="Studio" href="/admin/studio" isCollapsed={!shouldExpand} />
            )}
            <NavItem
              icon={<StoreIcon size={16} />}
              label="Portal"
              href="/admin/portal"
              isCollapsed={collapsed}
              matchSegments
              hasDropdown={true}
              dropdownItems={[
                { label: 'Total View', href: '/admin/portal/total' },
                { label: 'Style View', href: '/admin/portal/style' },
                { label: 'Categories', href: '/admin/portal/categories' },
              ]}
            />
            <NavItem
              icon={<FileTextIcon size={16} />}
              label="Reports"
              href="/admin/reports"
              isCollapsed={collapsed}
              matchSegments
              hasDropdown={true}
              dropdownItems={[
                { label: 'Sales', href: '/admin/reports/sales/transactions' },
                { label: 'Inventory', href: '/admin/reports/inventory' },
                { label: 'Returns', href: '/admin/reports/returns/transactions' },
              ]}
            />
            {/* The v2 portal, admin preview only (no DOM unless isV2PreviewEnabled). */}
            <AdminV2PortalLink isCollapsed={collapsed} />
          </div>

          {/* Operations */}
          <GroupLabel label="Operations" isCollapsed={collapsed} />
          <div className="space-y-0.5">
            {/* Catalog (product onboarding) — live for GB admins only (Ari,
                2026-09-10). Mounted inside THIS shell at /admin/catalog/*; the
                operator picks the brand in the bar above each catalog screen.
                Every admin can operate it, so no role check beyond the shell.
                The header opens /admin/catalog itself, so it has no "Catalog"
                child. Catalog Errors lives in this dropdown; with the flag off
                it falls back to its own row so the page stays reachable. */}
            {isCatalogEnabled() ? (
              <NavItem
                icon={<PackageIcon size={16} />}
                label="Catalog"
                href="/admin/catalog"
                isCollapsed={collapsed}
                matchSegments
                hasDropdown={true}
                dropdownHeaderLink={true}
                dropdownItems={[
                  { label: 'Marketplaces', href: '/admin/catalog/marketplaces' },
                  { label: 'Product Onboarding', href: '/admin/catalog/connect' },
                  { label: 'Tracking', href: '/admin/catalog/tracking' },
                  { label: 'Catalog Errors', href: '/admin/catalog-rejections' },
                ]}
              />
            ) : (
              <NavItem
                icon={<ShieldAlertIcon size={16} />}
                label="Catalog Errors"
                href="/admin/catalog-rejections"
                isCollapsed={collapsed}
                matchSegments
              />
            )}
            <NavItem
              icon={<InboxIcon size={16} />}
              label="Action Requests"
              href="/admin/action-requests"
              isCollapsed={collapsed}
              matchSegments
              badge={pendingRequests ? String(pendingRequests) : undefined}
            />
            <NavItem
              icon={<WarehouseIcon size={16} />}
              label="Warehouses"
              href="/admin/operations?tab=warehouses"
              isCollapsed={collapsed}
              matchSegments
              hasDropdown={true}
              dropdownItems={[
                { label: 'Overview', href: '/admin/operations?tab=warehouses' },
                { label: 'Nordstrom Ops', href: '/admin/operations?tab=nordstromOps' },
              ]}
            />
          </div>

          {/* Dev — pages that only exist in local dev, kept out of the real groups */}
          {import.meta.env.DEV && (
            <>
              <GroupLabel label="Dev" isCollapsed={collapsed} />
              <div className="space-y-0.5">
                <NavItem icon={<MegaphoneIcon size={16} />} label="Promotions" href="/admin/promotions" isCollapsed={collapsed} matchSegments />
                <NavItem icon={<ClipboardCheckIcon size={16} />} label="Brand Onboarding" href="/admin/onboarding" isCollapsed={collapsed} matchSegments />
              </div>
            </>
          )}

          {/* Settings — platform administration, pinned just above the profile.
              Brands and Users & Roles are open to every admin; System Health
              and User Activity stay SUPER_ADMIN-only. */}
          <div className="mt-auto pb-2">
            <GroupLabel label="Settings" isCollapsed={collapsed} />
            <div className="space-y-0.5">
              <NavItem icon={<Building2Icon size={16} />} label="Brands" href="/admin/brands" isCollapsed={collapsed} matchSegments />
              <NavItem icon={<UsersIcon size={16} />} label="Users & Roles" href="/admin/users" isCollapsed={collapsed} matchSegments />
              {isSuperAdmin() && (
                <>
                  <NavItem icon={<HeartPulseIcon size={16} />} label="System Health" href="/admin/system" isCollapsed={collapsed} matchSegments />
                  <NavItem icon={<HistoryIcon size={16} />} label="User Activity" href="/admin/activity" isCollapsed={collapsed} matchSegments />
                </>
              )}
            </div>
          </div>
        </nav>

        {/* User Profile Footer */}
        <div className="border-t border-white/30">
          <UserProfile isCollapsed={collapsed} />
        </div>
      </div>
    </div>
  );
};

export default AdminSidebar;
